/**
 * Cat Form Component - Add/Edit cats
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Cat, 
  Upload, 
  X, 
  Save,
  ArrowLeft,
  Plus,
  Trash2
} from 'lucide-react';
import { Card, Button, Input, Select, Textarea, Alert } from '../shared/UIComponents';
import { useForm } from '../../hooks/useAuth';
import { useAuth } from '../../hooks/useAuth';
import { createCat, updateCat, SA_CAT_BREEDS } from '../../firebase/firestore';

// Placeholder image
const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f4f6' width='200' height='200'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23d1d5db' font-family='system-ui' font-size='24'%3E🐱%3C/text%3E%3C/svg%3E";

const initialValues = {
  name: '',
  breed: '',
  ems: '',
  sex: '',
  dateOfBirth: '',
  color: '',
  microchip: '',
  registrationNumber: '',
  sireId: '',
  damId: '',
  status: 'not_for_sale',
  price: '',
  description: '',
  health: {
    vaccinations: [],
    tests: [],
    neutered: false
  }
};

const validateCat = (values) => {
  const errors = {};
  
  if (!values.name?.trim()) errors.name = 'Name is required';
  if (!values.breed) errors.breed = 'Breed is required';
  if (!values.sex) errors.sex = 'Sex is required';
  if (!values.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
  
  if (values.status === 'available' && !values.price) {
    errors.price = 'Price is required for available cats';
  }
  
  if (values.price && isNaN(Number(values.price))) {
    errors.price = 'Price must be a number';
  }
  
  return errors;
};

export const CatForm = ({ cat = null, onSuccess }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState(cat?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const isEditing = !!cat;

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue
  } = useForm(cat || initialValues, validateCat);

  const onSubmit = async (formValues) => {
    try {
      setSubmitError(null);
      
      const catData = {
        ...formValues,
        price: formValues.price ? Number(formValues.price) : null,
        photos,
        catteryId: user.catteryId,
        dateOfBirth: new Date(formValues.dateOfBirth)
      };

      if (isEditing) {
        await updateCat(cat.id, catData);
      } else {
        await createCat(catData);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/dashboard/cats');
      }
    } catch (error) {
      console.error('Error saving cat:', error);
      setSubmitError(error.message);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    // TODO: Implement actual file upload to Firebase Storage
    // For now, create object URLs for preview
    const newPhotos = files.map(file => URL.createObjectURL(file));
    setPhotos([...photos, ...newPhotos]);
    
    setUploading(false);
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  // Breed options
  const breedOptions = SA_CAT_BREEDS.map(b => ({
    value: b.code,
    label: `${b.name} (${b.code})`
  }));

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/cats')}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">
            {isEditing ? 'Edit Cat' : 'Add New Cat'}
          </h1>
          <p className="text-gray-500">
            {isEditing ? 'Update your cat\'s information' : 'Enter your cat\'s details'}
          </p>
        </div>
      </div>

      {submitError && (
        <Alert type="error" className="mb-6" onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(onSubmit); }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Photos Section */}
          <Card className="lg:col-span-1">
            <h2 className="font-semibold text-gray-900 mb-4">Photos</h2>
            
            {/* Photo Grid */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {photos.map((photo, index) => (
                <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img 
                    src={photo} 
                    alt={`Cat photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              
              {/* Upload Button */}
              <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-feline-400 hover:bg-feline-50 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {uploading ? (
                  <div className="animate-spin w-6 h-6 border-2 border-feline-500 border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Add Photo</span>
                  </>
                )}
              </label>
            </div>
            
            <p className="text-xs text-gray-500">
              Upload up to 6 photos. First photo will be the main image.
            </p>
          </Card>

          {/* Main Form */}
          <Card className="lg:col-span-2">
            <h2 className="font-semibold text-gray-900 mb-4">Basic Information</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Name *"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && errors.name}
                placeholder="e.g., Gatsby"
              />

              <Select
                label="Breed *"
                name="breed"
                value={values.breed}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.breed && errors.breed}
                options={breedOptions}
                placeholder="Select breed"
              />

              <Input
                label="EMS Code"
                name="ems"
                value={values.ems}
                onChange={handleChange}
                placeholder="e.g., MCO n 22"
              />

              <Select
                label="Sex *"
                name="sex"
                value={values.sex}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.sex && errors.sex}
                options={[
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' }
                ]}
                placeholder="Select sex"
              />

              <Input
                label="Date of Birth *"
                name="dateOfBirth"
                type="date"
                value={values.dateOfBirth}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dateOfBirth && errors.dateOfBirth}
              />

              <Input
                label="Color"
                name="color"
                value={values.color}
                onChange={handleChange}
                placeholder="e.g., Black Classic Tabby"
              />

              <Input
                label="Microchip Number"
                name="microchip"
                value={values.microchip}
                onChange={handleChange}
                placeholder="15-digit number"
              />

              <Input
                label="Registration Number"
                name="registrationNumber"
                value={values.registrationNumber}
                onChange={handleChange}
                placeholder="Registry number"
              />
            </div>

            {/* Status and Pricing */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Listing Status</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  label="Status"
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  options={[
                    { value: 'not_for_sale', label: 'Not for Sale' },
                    { value: 'available', label: 'Available' },
                    { value: 'reserved', label: 'Reserved' },
                    { value: 'sold', label: 'Sold' }
                  ]}
                />

                <Input
                  label={`Price (ZAR) ${values.status === 'available' ? '*' : ''}`}
                  name="price"
                  type="number"
                  value={values.price}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.price && errors.price}
                  placeholder="e.g., 15000"
                  disabled={values.status === 'not_for_sale'}
                />
              </div>

              {values.status === 'available' && (
                <Alert type="info" className="mt-4">
                  When this cat is marked as <strong>Sold</strong>, a 10% commission 
                  ({values.price ? `R${Math.round(Number(values.price) * 0.1).toLocaleString()}` : 'R0'}) 
                  will be added to your monthly ledger.
                </Alert>
              )}
            </div>

            {/* Description */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <Textarea
                label="Description"
                name="description"
                value={values.description}
                onChange={handleChange}
                rows={4}
                placeholder="Tell potential buyers about this cat's personality, temperament, and any special characteristics..."
              />
            </div>

            {/* Health Information */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Health Information</h3>
              
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="neutered"
                    checked={values.health?.neutered || false}
                    onChange={(e) => setFieldValue('health', { 
                      ...values.health, 
                      neutered: e.target.checked 
                    })}
                    className="w-4 h-4 rounded border-gray-300 text-feline-500 focus:ring-feline-500"
                  />
                  <span className="text-sm text-gray-700">Neutered/Spayed</span>
                </label>

                <div>
                  <p className="text-sm text-gray-500 mb-2">
                    Add vaccination and health test records in the cat's detailed view after saving.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 mt-6">
          <Button 
            type="button" 
            variant="ghost"
            onClick={() => navigate('/dashboard/cats')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            loading={isSubmitting}
          >
            <Save className="w-4 h-4" />
            {isEditing ? 'Update Cat' : 'Add Cat'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CatForm;
